/** Text-level cleanup for editor HTML. Keep in sync with functions/src/OpenAI/articleSanitize.ts. */

import {looksLikeGluedScrapePhrase, looksLikeScrapedNavTitle} from '@/service/articleMainKeyword';
import {filterScrapJunkContent, isScrapJunkLine, plainScrapText} from '@/service/scrapJunkFilter';
import {stripInvisibleCharacters} from '@/utils/string';

export {filterScrapJunkContent, isScrapJunkLine, looksLikeScrapeBoilerplateBody, sanitizeImportedImageAlt, scrapSectionHasBody} from '@/service/scrapJunkFilter';

const LOWER = 'a-záäčďéěíĺľňóôřšťúůýž';
const UPPER = 'A-ZÁÄČĎÉĚÍĹĽŇÓÔŘŠŤÚŮÝŽ';
const PREP_LETTERS = 'vkszouaiVKSZOUAI';

const NEVER_SPLIT = new Set([
    'voda', 'vody', 'vodě', 'vodou', 'vodní', 'včas', 'vždy', 'více', 'vlastní', 'vlastně',
    'senioři', 'senior', 'ideálně', 'ideální', 'investice', 'strava', 'stravy', 'skladovat',
    'skladování', 'krmení', 'krmivo', 'který', 'která', 'které', 'ale', 'ani', 'anebo',
    'ostrava', 'ostravě', 'syrová', 'syrové', 'zdraví', 'zvíře', 'zvířata',
]);

const REMAINDER_WORDS = new Set([
    'tomto', 'této', 'teto', 'tomuto', 'tohoto', 'těchto', 'tomu',
    'nás', 'nas', 'vás', 'nich', 'něm', 'ní', 'praxi', 'případě', 'rámci',
    'nádobách', 'nadobach', 'nádoby', 'nádobě', 'lednici', 'ledničce',
    'skladovat', 'skladování', 'vznik', 'vzniku', 'roce', 'letech',
    'česku', 'brně', 'ostravě', 'praze', 'souladu', 'podstatě',
]);

const INFINITIVE_RE = /(?:ovat|ávat|avat|nout|[aáeěiíoóuůyý]t)$/i;
const ACRONYMS = new Set(['BARF', 'FAQ', 'HTML', 'PDF', 'SEO', 'AI', 'URL', 'H2', 'H1']);

function foldKey(value: string): string {
    return value
        .toLocaleLowerCase('cs')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function mapPlainRuns(html: string, fn: (text: string) => string): string {
    return html.replace(/(<[^>]+>)|([^<]+)/g, (full, tag: string, text: string) => tag ? tag : fn(text || ''));
}

function shouldSplit(prep: string, rest: string): boolean {
    const token = foldKey(prep + rest);
    if (NEVER_SPLIT.has(token) || NEVER_SPLIT.has((prep + rest).toLocaleLowerCase('cs'))) {
        return false;
    }
    const restKey = foldKey(rest);
    if (REMAINDER_WORDS.has(restKey) || REMAINDER_WORDS.has(rest.toLocaleLowerCase('cs'))) {
        return true;
    }
    return prep.toLocaleLowerCase('cs') === 'a' && rest.length >= 5 && INFINITIVE_RE.test(rest);
}

export function fixCzechGluedPrepositions(raw: string): string {
    const source = String(raw || '');
    if (!source) {
        return '';
    }
    return mapPlainRuns(source, (plain) => {
        let text = plain.replace(/[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]{6,}/g, (token) => {
            for (const prep of ['PŘED', 'PŘES', 'PODLE', 'MEZI', 'PŘI', 'PRO', 'NAD', 'POD', 'BEZ']) {
                const idx = token.indexOf(prep);
                if (idx >= 4) {
                    const after = token.slice(idx + prep.length);
                    return `${token.slice(0, idx)} ${prep}${after ? ` ${after}` : ''}`;
                }
            }
            return token;
        });
        text = text.replace(
            new RegExp(`(^|[\\s\\(\\[„"'>])([${PREP_LETTERS}])([a-záäčďéěíĺľňóôřšťúůýžA-ZÁÄČĎÉĚÍĹĽŇÓÔŘŠŤÚŮÝŽ]{2,})`, 'g'),
            (all, lead: string, prep: string, rest: string) => {
                const token = foldKey(prep + rest);
                if (NEVER_SPLIT.has(token) || NEVER_SPLIT.has((prep + rest).toLocaleLowerCase('cs'))) {
                    return all;
                }
                // A capital remainder is a new word only when it is a known glued
                // word (vPraxi). An internal capital stays inside the token.
                if (shouldSplit(prep, rest)) {
                    return `${lead}${prep} ${rest}`;
                }
                return all;
            }
        );
        return text.replace(/[^\S\n]{2,}/g, ' ');
    });
}

/**
 * A lowercase letter plus a capital is a word boundary only when the capital
 * does not continue the same token. "přechodV praxi" still breaks, because
 * that capital is its own word.
 */
function internalCapitalBoundary(): RegExp {
    return new RegExp(`([${LOWER}])([${UPPER}])(?![${LOWER}${UPPER}])`, 'g');
}

export function splitGluedHeadingBody(raw: string): string {
    const source = String(raw || '');
    if (!source) {
        return '';
    }
    const boundary = internalCapitalBoundary();
    const firstBreak = source.search(/\n\n|<\/p>|<br\s*\/?>/i);
    const headLen = firstBreak > 0 ? Math.min(firstBreak, 200) : Math.min(source.length, 200);
    const head = source.slice(0, headLen);
    const rest = source.slice(headLen);
    const match = head.match(new RegExp(`^([^.!?]{10,80}?)([${LOWER}])([${UPPER}])(?![${LOWER}${UPPER}])`));
    if (match) {
        return `${match[1]}${match[2]}\n\n${match[3]}${head.slice(match[0].length)}${rest}`;
    }
    boundary.lastIndex = 0;
    return source.replace(boundary, '$1 $2');
}

export function sentenceCaseHeading(raw: string): string {
    const text = String(raw || '').replace(/\s+/g, ' ').trim();
    const letters = text.replace(new RegExp(`[^${LOWER}${UPPER}]`, 'g'), '');
    if (letters.length <= 4) {
        return text;
    }
    const uppers = (letters.match(new RegExp(`[${UPPER}]`, 'g')) || []).length;
    if (uppers < letters.length * 0.75) {
        return text;
    }
    const parts = text.split(/(\s+)/);
    let firstWord = true;
    return parts.map((part) => {
        if (!part.trim()) {
            return part;
        }
        const stripped = part.replace(new RegExp(`[^${LOWER}${UPPER}0-9]`, 'g'), '');
        const upper = stripped.toLocaleUpperCase('cs');
        if (ACRONYMS.has(upper) && stripped.length <= 6) {
            firstWord = false;
            return part.replace(stripped, upper);
        }
        const lower = part.toLocaleLowerCase('cs');
        if (firstWord) {
            firstWord = false;
            const firstLetter = lower.match(new RegExp(`[${LOWER}${UPPER}]`));
            if (!firstLetter) {
                return lower;
            }
            const idx = lower.indexOf(firstLetter[0]);
            return lower.slice(0, idx) + firstLetter[0].toLocaleUpperCase('cs') + lower.slice(idx + 1);
        }
        return lower;
    }).join('');
}

function asPlain(value: unknown): string {
    return stripInvisibleCharacters(String(value || '').replace(/\s+/g, ' ').trim());
}

export function normalizeHeadingKey(value: unknown): string {
    return asPlain(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}

/** Keep in sync with functions/src/OpenAI/articleSanitize.ts stripMarkdownArtifacts. */
export function stripMarkdownArtifacts(raw: unknown): string {
    let text = String(raw || '').replace(/\r\n/g, '\n');
    if (!text) {
        return '';
    }
    text = text.replace(/```[\s\S]*?```/g, ' ');
    text = text.replace(/^\s{0,3}#{1,6}(?=\s|#|$)/gm, '');
    text = text.replace(/(^|\s)#{1,6}\s+/g, '$1');
    text = text.replace(/#{2,}/g, '');
    text = text.replace(/(^|\s)#(?=\s|$)/g, '$1');
    text = text.replace(/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/gm, '');
    text = text.replace(/(?:^|\n)\s*(?:-{3,}|\*{3,}|_{3,})\s+/g, '\n');
    text = text.replace(/\s+(?:-{3,}|\*{3,}|_{3,})(?=\s|$)/g, ' ');
    return text.replace(/[^\S\n]{2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

export type HeadingRole = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'question' | 'faq' | 'meta';

export function isGrammaticalQuestion(text: unknown): boolean {
    const value = asPlain(text);
    if (!value) {
        return false;
    }
    const words = value.split(' ').filter(Boolean).length;
    if (/[?？]\s*$/.test(value) && words >= 3) {
        return true;
    }
    return words >= 3 && /^(jak|co je|co jsou|proč|proc|kdy|kde|kdo|kam|čím|cim|what|why|how|when|where|who|is|are|does|do|can)\b/i.test(value);
}

export function isRawKeywordHeading(heading: unknown, keyword: unknown): boolean {
    const headingKey = normalizeHeadingKey(heading);
    const keywordKey = normalizeHeadingKey(keyword);
    if (!headingKey) {
        return true;
    }
    if (!keywordKey) {
        return false;
    }
    return headingKey === keywordKey;
}

function prettyTopic(keyword: string): string {
    const text = asPlain(keyword);
    if (!text) {
        return '';
    }
    if (text.length <= 4 && /^[a-z0-9]+$/i.test(text)) {
        return text.toUpperCase();
    }
    return text;
}

export function naturalHeadingForTopic(
    keyword: unknown,
    opts: {mainKeyword?: unknown; language?: string; role?: HeadingRole} = {}
): string {
    const topic = asPlain(keyword);
    if (!topic) {
        return '';
    }
    if (isGrammaticalQuestion(topic)) {
        return topic;
    }
    const pretty = prettyTopic(topic);
    const main = asPlain(opts.mainKeyword);
    const czech = !opts.language || /^(cs|czech)/i.test(String(opts.language));
    const role = opts.role || 'h2';
    // "Jak souvisí X s jak vybrat matraci" is broken Czech: only link to the
    // main keyword when it is a noun phrase, not a how/what query.
    const mainIsQuery = /^(jak|co|proč|proc|kdy|kde|kam|kolik|kter[ýáéí]|čím|cim|how|what|why|when|where|which|who)\b/i.test(main);
    const mainLooksLikeScrapeTitle = looksLikeScrapedNavTitle(main, topic);
    const relatedToMain = Boolean(main && !mainIsQuery && !mainLooksLikeScrapeTitle && normalizeHeadingKey(main) !== normalizeHeadingKey(topic));
    const topicHasMain = relatedToMain && normalizeHeadingKey(topic).includes(normalizeHeadingKey(main));
    const faq = role === 'question' || role === 'faq';
    const topicKey = normalizeHeadingKey(topic);
    const mainKey = normalizeHeadingKey(main);
    if (czech) {
        if (faq) {
            if (mainKey && topicKey === mainKey && mainIsQuery) {
                const q = pretty.charAt(0).toLocaleUpperCase('cs') + pretty.slice(1);
                return q.endsWith('?') ? q : `${q}?`;
            }
            if (topicHasMain) {
                return `Jak funguje ${pretty}?`;
            }
            if (relatedToMain) {
                return `Jak souvisí ${pretty} s ${main}?`;
            }
            return `Co je ${pretty} a jak to funguje?`;
        }
        if (role === 'h1') {
            return `Jak funguje ${pretty}`;
        }
        if (topicHasMain) {
            return `Jak funguje ${pretty}`;
        }
        if (relatedToMain) {
            return `Jak souvisí ${pretty} s ${main}`;
        }
        return `Jak funguje ${pretty}`;
    }
    if (faq) {
        if (topicHasMain) {
            return `How does ${pretty} work?`;
        }
        if (relatedToMain) {
            return `How does ${pretty} relate to ${main}?`;
        }
        return `What is ${pretty} and how does it work?`;
    }
    if (role === 'h1') {
        return `How ${pretty} works`;
    }
    if (topicHasMain) {
        return `How ${pretty} works`;
    }
    if (relatedToMain) {
        return `How ${pretty} relates to ${main}`;
    }
    return `How ${pretty} works`;
}

/**
 * True when a heading is the new-article template for a raw keyword
 * ("Jak funguje …", "Jak souvisí … s …", and the English equivalents).
 * A heading that already existed on the page is not manufactured.
 */
export function isManufacturedOutlineHeading(
    heading: unknown,
    keyword: unknown,
    opts: {mainKeyword?: unknown; language?: string; role?: HeadingRole} = {}
): boolean {
    const text = asPlain(heading);
    const topic = asPlain(keyword);
    if (!text || !topic) {
        return false;
    }
    const textKey = normalizeHeadingKey(text);
    const topicKey = normalizeHeadingKey(topic);
    if (!textKey || textKey === topicKey) {
        return false;
    }
    const languages = [opts.language, 'cs', 'en'];
    return languages.some((language) => {
        const made = naturalHeadingForTopic(topic, {...opts, language});
        const madeKey = normalizeHeadingKey(made);
        return !!madeKey && textKey === madeKey;
    });
}

/**
 * URL-improve extra slot: keep a heading the model already wrote.
 * A blank, a raw keyword, or a Jak souvisí / Jak funguje template is dropped.
 * This does not invent a replacement heading.
 */
export function headingForUrlImproveSlot(
    heading: unknown,
    keyword: unknown,
    opts: {mainKeyword?: unknown; language?: string; role?: HeadingRole} = {}
): string {
    const text = asPlain(stripMarkdownArtifacts(heading));
    if (!text) {
        return '';
    }
    if (isRawKeywordHeading(text, keyword) || isRawKeywordHeading(text, opts.mainKeyword)) {
        return '';
    }
    if (isManufacturedOutlineHeading(text, keyword, opts) || isManufacturedOutlineHeading(text, opts.mainKeyword, opts)) {
        return '';
    }
    return text;
}

export function ensureNaturalHeading(
    heading: unknown,
    keyword: unknown,
    opts: {mainKeyword?: unknown; language?: string; role?: HeadingRole} = {}
): string {
    const cleaned = asPlain(stripMarkdownArtifacts(heading));
    const main = asPlain(opts.mainKeyword);
    if (cleaned && (looksLikeGluedScrapePhrase(cleaned) || looksLikeScrapedNavTitle(cleaned, main))) {
        return naturalHeadingForTopic(keyword || opts.mainKeyword || cleaned, opts) || cleaned;
    }
    const fixed = asPlain(fixCzechGluedPrepositions(cleaned));
    if (fixed && looksLikeGluedScrapePhrase(fixed)) {
        return naturalHeadingForTopic(keyword || opts.mainKeyword || fixed, opts) || fixed;
    }
    if (fixed && !isRawKeywordHeading(fixed, keyword) && !isRawKeywordHeading(fixed, opts.mainKeyword)) {
        return fixed;
    }
    return naturalHeadingForTopic(keyword || fixed || cleaned, opts) || fixed || cleaned;
}

export function sanitizeImportedParagraph(body: unknown, mainKeyword = ''): string {
    return filterScrapJunkContent(body, mainKeyword);
}

export function isCompleteFaqPair(question: unknown, answer: unknown): boolean {
    const q = asPlain(stripMarkdownArtifacts(question));
    const a = asPlain(stripMarkdownArtifacts(answer));
    return !!(q && a);
}

export function countIncompleteFaqRows(
    questions: Array<{question?: unknown; answer?: unknown}> | null | undefined
): number {
    return (questions || []).filter((item) => {
        const q = asPlain(stripMarkdownArtifacts(item.question));
        const a = asPlain(stripMarkdownArtifacts(item.answer));
        return (q || a) && !isCompleteFaqPair(item.question, item.answer);
    }).length;
}

/**
 * Production import of one scraped heading level.
 * Same steps as setArticleScrapedContent: drop a heading sanitize rejects,
 * keep the paragraph that belongs to the heading that survived.
 */
export function importScrapedBlocks(
    headings: unknown[] | null | undefined,
    paragraphs: unknown[] | null | undefined,
    mainKeyword: string,
    role: HeadingRole = 'h2'
): Array<{h: string; p: string}> {
    const heads = headings || [];
    const paras = paragraphs || [];
    const count = Math.max(heads.length, paras.length);
    const out: Array<{h: string; p: string}> = [];
    for (let index = 0; index < count; index++) {
        const heading = sanitizeImportedHeading(heads[index], mainKeyword, {role});
        if (!heading) {
            continue;
        }
        out.push({
            h: heading,
            p: sanitizeImportedParagraph(paras[index], mainKeyword),
        });
    }
    return out;
}

/** Drop or repair scraped headings before they become editor slots. */
export function sanitizeImportedHeading(
    heading: unknown,
    mainKeyword: string,
    opts: {language?: string; role?: HeadingRole} = {}
): string | null {
    const role = opts.role || 'h2';
    const stripped = plainScrapText(stripMarkdownArtifacts(heading));
    const fixed = asPlain(fixCzechGluedPrepositions(stripped));
    if (!fixed) {
        return null;
    }
    const keywordOnly = !!mainKeyword && normalizeHeadingKey(fixed) === normalizeHeadingKey(mainKeyword);
    if ((isScrapJunkLine(stripped) || isScrapJunkLine(fixed)) && !(role === 'h1' && keywordOnly)) {
        return null;
    }
    // Keep the on-page heading. A dash in a section title is not a reason to
    // invent "Jak funguje" / "Jak souvisí". A glued token is not a heading.
    if (role === 'h1') {
        return fixed;
    }
    if (looksLikeGluedScrapePhrase(fixed)) {
        return null;
    }
    return fixed;
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Keep the first complete H1 heading+lead; drop a pasted duplicate. */
export function collapseDuplicateHeadingLead(heading: unknown, body: unknown): string {
    let text = String(body || '').replace(/\r\n/g, '\n').trim();
    if (!text) {
        return '';
    }
    const headingText = asPlain(heading);
    const hKey = normalizeHeadingKey(headingText);
    if (hKey) {
        text = text.replace(/^\s{0,3}#{1,6}\s+/, '').replace(/^<h1\b[^>]*>[\s\S]*?<\/h1>\s*/i, '');
        const blocks = text.split(/\n{2,}/);
        const firstBlock = (blocks[0] || text.split(/\n/)[0] || '').trim();
        const firstKey = normalizeHeadingKey(firstBlock.replace(/<[^>]+>/g, ' '));
        if (firstKey && (firstKey === hKey || (firstKey.startsWith(hKey) && firstBlock.length <= headingText.length + 12))) {
            text = (blocks.length > 1 ? blocks.slice(1).join('\n\n') : text.slice(firstBlock.length)).replace(/^\s+/, '');
        } else if (headingText.length >= 8) {
            text = text.replace(new RegExp('^' + escapeRegExp(headingText) + '(?:\\s+|(?=[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]))', 'i'), '').trim();
        }
    }
    const parts = text.split(/\n{2,}/).map((item) => item.trim()).filter(Boolean);
    if (hKey && parts.length >= 2) {
        const secondHeadingAt = parts.findIndex((block, index) =>
            index > 0 && normalizeHeadingKey(block.replace(/<[^>]+>/g, ' ')) === hKey
        );
        if (secondHeadingAt > 0) {
            return parts.slice(0, secondHeadingAt).join('\n\n');
        }
    }
    if (parts.length >= 2 && parts.length % 2 === 0) {
        const mid = parts.length / 2;
        const left = parts.slice(0, mid).join('\n\n');
        const right = parts.slice(mid).join('\n\n');
        if (normalizeHeadingKey(left) && normalizeHeadingKey(left) === normalizeHeadingKey(right)) {
            text = left;
        }
    }
    const result = text.trim();
    const originalPlain = asPlain(String(body || '').replace(/<[^>]+>/g, ' '));
    if (!asPlain(result.replace(/<[^>]+>/g, ' ')) && originalPlain.length >= 40) {
        return String(body || '').replace(/\r\n/g, '\n').trim();
    }
    return result;
}
